import React, {
    useContext, useEffect, useState
}                                     from 'react'
import {
    Box,
    Button,
    Container,
    FormGroup,
    Grid,
    Paper,
    Stack, TextField,
    Typography,
}                                     from '@mui/material'
import { useTranslation }             from 'react-i18next'
import { useNavigate, useParams }     from 'react-router-dom'
import { useApi }                     from '../utils/api'
import { useSnackbar }                from 'notistack'
import AlertSimpleDialog              from '../ui/components/alert-simple-dialog'
import Page                           from '../ui/components/Page'
import { BaseBreadcrumbs }            from '../ui/components/BaseBreadcrumbs'
import ShortFormControl               from '../ui/components/short-form-control'
import {
    dateFromInputStringToISO,
    dateToInputString
}                                     from '../utils/textutils'
import { DateTime }                   from 'luxon'
import { AppContext }                 from '../App'

export default function ServicePeriodPage() {

    const api = useApi()
    const { user } = useContext(AppContext)
    const navigate = useNavigate()
    const { serviceId, servicePeriodId } = useParams()
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()
    const [service, setService] = useState(null)
    const [values, setValues] = useState({
        validFrom: dateToInputString(DateTime.now().toISO()),
        validTo: dateToInputString(DateTime.now().plus({ years: 100 }).toISO()),
        pattern: '',
    })
    const [shownDeleteDialog, setShownDeleteDialog] = useState(false)

    useEffect(() => {
        if (!isNaN(serviceId)) {
            api.service.detail(serviceId).then(data => setService(data)).catch(e => console.error(e))
        }
    }, [serviceId])

    useEffect(() => {
        (async () => {
            if (!isNaN(servicePeriodId)) {
                try{
                    let servicePeriod = await api.servicePeriod.detail(servicePeriodId)
                    setValues({
                        ...servicePeriod,
                        validFrom: dateToInputString(servicePeriod.validFrom),
                        validTo: dateToInputString(servicePeriod.validTo),
                    })
                } catch (e){
                    enqueueSnackbar(t('Can\'t load service period'), {
                        variant: "error"
                    })
                }
            }
        })()
    }, [servicePeriodId])

    const handleChange = (prop) => (event) => {
        setValues({ ...values, [prop]: event.target.value })
    }

    const handleChangeDirect = (prop, value) => {
        setValues({ ...values, [prop]: value })
    }

    const handleCloseDeleteDialog = (result) => {
        setShownDeleteDialog(false)
        if (result) {
            api.servicePeriod.delete(servicePeriodId).then(() => {
                enqueueSnackbar(t('Service period deleted'), {
                    variant: 'success'
                })
                navigate(`/services/${serviceId}`)
            }).catch(error => {
                enqueueSnackbar(t('Failed to delete service period'), {
                    variant: 'error'
                })
            })
        }
    }

    const onSubmit = async (e) => {
        e.preventDefault()
        let data = {
            ...values,
            serviceId: serviceId,
            validFrom: dateFromInputStringToISO(values.validFrom),
            validTo: dateFromInputStringToISO(values.validTo),
        }
        try {
            let servicePeriod = await api.servicePeriod.save(data)
            navigate(`/services/${serviceId}/service-period/${servicePeriod.id}`)
            enqueueSnackbar(t('Service period saved'), {
                variant: 'success'
            })
        } catch (e) {
            enqueueSnackbar(t('Failed to save service period'), {
                variant: 'error'
            })
        }
    }

    return (
        <Page title={t('Service period')}>
            <Container component="main">
                <BaseBreadcrumbs key={'breadcrumb-service-period'}
                                 title={t('Service period')}
                                 items={[
                                     { title: t('Services'), href: `/services` },
                                     { title: service?.title ?? t('Service'), href: `/services/${serviceId}` }
                                 ]}/>
                <Stack direction={'row'} spacing={2}>
                    <Typography variant={'h6'} pl={2}>{service?.title}</Typography>
                </Stack>
                <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
                    <Paper sx={{ p: 2, width: '100%' }}>
                        <Grid container sx={{ width: '100%' }} spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    id="pattern"
                                    name="pattern"
                                    multiline={true}
                                    label={t('Pattern')}
                                    variant="standard"
                                    value={values.pattern}
                                    onChange={handleChange('pattern')}
                                    sx={{ width: '100%' }}/>
                                <Stack direction={'row'} justifyContent={'end'}>
                                    <Typography variant={'caption'}>{t(`helper-serviceOperatorPattern`)}</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormGroup>
                                    <ShortFormControl
                                        name={'validFrom'}
                                        title={t('Valid from')}
                                        value={values.validFrom}
                                        handleChange={handleChange}
                                        inputProps={{
                                            type: 'datetime-local',
                                        }}
                                    />
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormGroup>
                                    <ShortFormControl
                                        name={'validTo'}
                                        title={t('Valid to')}
                                        value={values.validTo}
                                        handleChange={handleChange}
                                        inputProps={{
                                            type: 'datetime-local',
                                        }}
                                    />
                                </FormGroup>
                            </Grid>
                        </Grid>
                    </Paper>
                    <Box sx={{ mt: 2, px: 1 }}>
                        <Button type={'submit'} variant={'contained'} disabled={user?.readonly}>{t('Save')}</Button>
                        { !isNaN(servicePeriodId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} disabled={user?.readonly} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
                    </Box>
                </Grid>
                <AlertSimpleDialog
                    title={t('Delete service period')}
                    message={t('delete-confirm-msg', { name: values.pattern })}
                    open={shownDeleteDialog}
                    onClose={handleCloseDeleteDialog}
                />
            </Container>
        </Page>
    )
}